/**
 * EPIC-DM01-D: Demo Results Page (Redesign — PDF-equivalent)
 *
 * Shows rich analysis results matching the PDF report's content hierarchy:
 *   Header → Fault Frame + Summary → Key Metrics → Primary Focus →
 *   Impact + Root Cause → Swing Thoughts → Practice Plan + Drill →
 *   Benefits → CTAs
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DemoButton } from '@/components/demo/ui';
import { ShareModal } from '@/components/demo/ShareModal';
import { useDemoMode, type DemoSession } from '@/hooks/useDemoMode';
import { useDemoResults } from '@/hooks/useDemoResults';
import {
  CheckCircle,
  Mail,
  Share2,
  ArrowRight,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Target,
} from 'lucide-react';

export default function DemoResults() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const {
    canAccessDemoMode,
    isDemoModeEnabled,
    isLoading: isLoadingConfig,
    getSession,
    sendEmail,
  } = useDemoMode();

  const {
    viewModel,
    playbackUrl,
    shareEligible,
    faultFrameUrl,
    isLoading: isLoadingResults,
    error: resultsError,
    privacyMode,
    togglePrivacyMode,
    trackActivateClick,
    trackShareClick,
  } = useDemoResults(sessionId || '');

  const [session, setSession] = useState<DemoSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    async function loadSession() {
      if (!sessionId) {
        setSessionError('No session ID provided');
        setIsLoadingSession(false);
        return;
      }
      try {
        const loadedSession = await getSession(sessionId);
        if (!loadedSession) {
          setSessionError('Session not found');
        } else {
          setSession(loadedSession);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        setSessionError('Failed to load session');
      } finally {
        setIsLoadingSession(false);
      }
    }
    loadSession();
  }, [sessionId, getSession]);

  const handleEmailReport = async () => {
    if (!sessionId || isSendingEmail) return;
    setIsSendingEmail(true);
    setEmailError(null);
    try {
      const emailResult = await sendEmail(sessionId);
      if (!emailResult.ok) throw new Error('Failed to send email');
      const maskedEmail = viewModel?.prospectName
        ? encodeURIComponent(`${viewModel.prospectName}'s email`)
        : '';
      navigate(`/demo/email-sent/${sessionId}?email=${maskedEmail}`);
    } catch (err) {
      console.error('[DemoResults] Email report failed:', err);
      setEmailError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleActivate = () => {
    trackActivateClick();
    navigate(`/activate/demo?session=${sessionId}`);
  };

  const handleShare = () => {
    trackShareClick();
    setIsShareModalOpen(true);
  };

  /* ── Loading / Error states ──────────────────────────────────── */
  if (isLoadingConfig || isLoadingSession || isLoadingResults) {
    return (
      <div className="demo-mode min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-[#196644]" />
          <p className="text-base text-gray-500">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!canAccessDemoMode || !isDemoModeEnabled) {
    return (
      <div className="demo-mode min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl p-8 shadow-lg">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-500 mb-4">You do not have permission to access Demo Mode.</p>
          <DemoButton variant="outline" onClick={() => navigate('/home')}>Return to Home</DemoButton>
        </div>
      </div>
    );
  }

  if (sessionError || !session || resultsError) {
    return (
      <div className="demo-mode min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl p-8 shadow-lg">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p className="text-gray-500 mb-4">
            {sessionError || resultsError?.message || 'Unable to load results'}
          </p>
          <DemoButton variant="outline" onClick={() => navigate('/demo')}>Return to Demo</DemoButton>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  /* ── Section heading helper ──────────────────────────────────── */
  const SectionHeading = ({ children }: { children: React.ReactNode }) => (
    <h3 className="text-base font-bold text-[#196644] border-b-2 border-[#196644] pb-1 mb-3">
      {children}
    </h3>
  );

  /* ── Main render ─────────────────────────────────────────────── */
  return (
    <div className="demo-mode min-h-screen bg-gray-50">
      {/* ══════════════════ HEADER ══════════════════ */}
      <header className="bg-gradient-to-r from-[#0D382A] to-[#0A4A22] text-white">
        <div className="max-w-2xl mx-auto px-5 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <img
                src="/nodoubles_logo_trans.png"
                alt="No Doubles Golf Co"
                className="h-7 w-auto mb-2"
              />
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-[#A7F3A1]" />
                <span className="text-sm text-[#A7F3A1]">Analysis Complete</span>
              </div>
            </div>
            <div className="text-right">
              {viewModel?.prospectName && (
                <p className="text-2xl font-bold leading-tight">{viewModel.prospectName}</p>
              )}
              <p className="text-[#A7F3A1] text-sm">Swing Analysis Report</p>
              <p className="text-gray-400 text-xs mt-0.5">{today}</p>
            </div>
            <button
              onClick={togglePrivacyMode}
              className="mt-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label={privacyMode ? 'Show details' : 'Hide details for privacy'}
            >
              {privacyMode ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* ══════════════════ CONTENT ══════════════════ */}
      <main className="max-w-2xl mx-auto px-5 py-6 space-y-6 pb-12">

        {privacyMode ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center text-gray-400">
            <EyeOff className="h-8 w-8 mx-auto mb-3" />
            <p>Details hidden for privacy. Tap the eye icon to reveal.</p>
          </div>
        ) : (
          <>
            {/* ── Fault Frame + What We Found ──────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Fault Frame */}
              <div>
                {faultFrameUrl ? (
                  <div className="border-2 border-gray-300 rounded-xl overflow-hidden bg-gray-50">
                    <img
                      src={faultFrameUrl}
                      alt="Key moment identified by AI"
                      className="w-full h-auto object-cover"
                      style={{ maxHeight: '320px' }}
                    />
                  </div>
                ) : (
                  <div className="border-2 border-gray-300 rounded-xl bg-gray-50 flex items-center justify-center" style={{ height: '280px' }}>
                    <span className="text-gray-400 italic text-sm">Fault Frame</span>
                  </div>
                )}
                <p className="text-center text-gray-500 italic text-[10px] mt-1.5">
                  Key moment identified by AI
                </p>
              </div>

              {/* What We Found + Key Metrics */}
              <div>
                <SectionHeading>What We Found</SectionHeading>
                <p className="text-sm text-[#1E293B] leading-relaxed mb-5">
                  {viewModel?.coachingSummary
                    ?? viewModel?.focusArea?.description
                    ?? 'Your swing analysis has been completed.'}
                </p>

                <SectionHeading>Key Metrics</SectionHeading>
                <div className="grid grid-cols-3 gap-2">
                  {/* Severity */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                    <div className="h-2 bg-[#DC2626]" />
                    <div className="p-2.5 text-center">
                      <div className="text-[9px] text-gray-500 mb-0.5">SEVERITY</div>
                      <div className="text-base font-bold">{viewModel?.severity ?? 'Low'}</div>
                    </div>
                  </div>
                  {/* Timeline */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                    <div className="h-2 bg-[#F9C94E]" />
                    <div className="p-2.5 text-center">
                      <div className="text-[9px] text-gray-500 mb-0.5">TIMELINE</div>
                      <div className="text-sm font-bold">{viewModel?.fixTimeline ?? '2-4 Weeks'}</div>
                    </div>
                  </div>
                  {/* Frequency */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                    <div className="h-2 bg-[#3B82F6]" />
                    <div className="p-2.5 text-center">
                      <div className="text-[9px] text-gray-500 mb-0.5">FREQUENCY</div>
                      <div className="text-sm font-bold">{viewModel?.practiceFrequency ?? '3x/Week'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Primary Focus Banner ─────────────────────── */}
            <div className="bg-gradient-to-r from-[#0D5C2B] to-[#196644] rounded-2xl p-5 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="h-7 w-7 text-[#196644]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-[#A7F3A1] tracking-wide mb-1">YOUR PRIMARY FOCUS</div>
                  <h2 className="text-lg font-bold text-white leading-tight mb-1">
                    {viewModel?.focusArea?.title ?? 'Swing Improvement'}
                  </h2>
                  <p className="text-sm text-white/90">
                    {viewModel?.primaryFocusDetail ?? viewModel?.focusArea?.description ?? ''}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Impact + Root Cause ──────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Impact on Your Game */}
              <div>
                <SectionHeading>Impact on Your Game</SectionHeading>
                {viewModel?.impactOnGame ? (
                  <div className="bg-[#FEF2F2] border-l-4 border-[#DC2626] rounded-r-lg p-4">
                    <p className="text-sm text-[#1E293B] leading-relaxed">{viewModel.impactOnGame}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Activate your account for detailed impact analysis.</p>
                )}
              </div>

              {/* Root Cause Analysis */}
              <div>
                <SectionHeading>Root Cause Analysis</SectionHeading>
                {viewModel?.rootCauseSummary && (
                  <p className="text-[11px] italic text-[#64748B] mb-3">{viewModel.rootCauseSummary}</p>
                )}
                {viewModel?.rootCauses && viewModel.rootCauses.length > 0 ? (
                  <div className="space-y-2">
                    {viewModel.rootCauses.slice(0, 2).map((cause, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#196644] text-white text-[11px] font-bold flex-shrink-0">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-[13px] font-bold text-black">{cause.title}</h4>
                            {cause.relationship && (
                              <p className="text-[10px] italic text-[#64748B] mb-0.5">({cause.relationship})</p>
                            )}
                            <p className="text-[11px] text-[#334155]">{cause.evidence}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Root cause details available in your full report.</p>
                )}
              </div>
            </div>

            {/* ── Swing Thoughts ───────────────────────────── */}
            {viewModel?.swingThoughts && viewModel.swingThoughts.length > 0 && (
              <div>
                <SectionHeading>Swing Thoughts</SectionHeading>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {viewModel.swingThoughts.slice(0, 2).map((thought, idx) => (
                    <div key={idx} className="bg-[#F0FDF4] border-l-4 border-[#196644] rounded-r-lg p-4">
                      <p className="text-[13px] text-[#1E293B] leading-relaxed">{thought}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Practice Plan + Featured Drill ──────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Weekly Practice Plan */}
              {viewModel?.weeklySchedule && viewModel.weeklySchedule.length > 0 && (
                <div>
                  <SectionHeading>Your Weekly Practice Plan</SectionHeading>
                  <div className="space-y-3">
                    {viewModel.weeklySchedule.slice(0, 3).map((ws, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#196644] text-white text-[13px] font-bold flex-shrink-0">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-bold text-black mb-1.5">{ws.session}</h4>
                            <ul className="space-y-1">
                              {ws.drills.map((drill, di) => (
                                <li key={di} className="flex items-start gap-1.5">
                                  <span className="text-[11px] text-[#196644] mt-0.5 flex-shrink-0">&#8226;</span>
                                  <span className="text-[11px] text-[#334155]">{drill}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Featured Drill */}
              {viewModel?.drills && viewModel.drills.length > 0 && (
                <div>
                  <SectionHeading>Featured Drill: {viewModel.drills[0].title}</SectionHeading>
                  {viewModel.drills[0].why && (
                    <p className="text-[11px] italic text-[#64748B] mb-3">{viewModel.drills[0].why}</p>
                  )}
                  {viewModel.drills[0].steps && viewModel.drills[0].steps.length > 0 && (
                    <ol className="space-y-2 mb-3">
                      {viewModel.drills[0].steps.map((step, si) => (
                        <li key={si} className="flex items-start gap-2">
                          <span className="text-[11px] font-bold text-[#196644] flex-shrink-0">{si + 1}.</span>
                          <span className="text-[11px] text-[#334155]">{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                  <div className="bg-[#F0FDF4] border border-[#196644] rounded-lg p-3 text-center">
                    <p className="text-[10px] text-[#0D5C2B]">See all drill details in your account</p>
                  </div>
                </div>
              )}
            </div>

            {/* ── What You'll Gain ─────────────────────────── */}
            {viewModel?.benefits && viewModel.benefits.length > 0 && (
              <div>
                <h3 className="text-base font-bold text-[#196644] border-b-2 border-[#196644] pb-1 mb-4 text-center">
                  What You'll Gain
                </h3>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {viewModel.benefits.slice(0, 5).map((benefit, idx) => (
                    <div key={idx} className="text-center">
                      <div className="w-10 h-10 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-2">
                        <CheckCircle className="h-5 w-5 text-[#0D5C2B]" />
                      </div>
                      <p className="text-[10px] text-[#334155] leading-tight">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Email Error ─────────────────────────────────── */}
        {emailError && (
          <div className="bg-[#FEF2F2] border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-600 text-sm">Failed to send email</p>
                <p className="text-sm text-red-600/80">{emailError}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── CTAs ────────────────────────────────────────── */}
        <div className="space-y-3 pt-2">
          <DemoButton
            variant="primary"
            fullWidth
            onClick={handleActivate}
            iconRight={<ArrowRight className="h-5 w-5" />}
          >
            Activate Account
          </DemoButton>

          {shareEligible && (
            <DemoButton
              variant="secondary"
              fullWidth
              onClick={handleShare}
              iconLeft={<Share2 className="h-5 w-5" />}
            >
              Share My Results
            </DemoButton>
          )}

          <DemoButton
            variant="ghost"
            fullWidth
            loading={isSendingEmail}
            onClick={handleEmailReport}
            iconLeft={!isSendingEmail ? <Mail className="h-5 w-5" /> : undefined}
          >
            {isSendingEmail ? 'Sending Report...' : 'Email My Report'}
          </DemoButton>
        </div>
      </main>

      {/* ── Share Modal ───────────────────────────────────── */}
      {sessionId && (
        <ShareModal
          sessionId={sessionId}
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          onEmailReport={handleEmailReport}
          initialPreview={{
            insight: viewModel?.top2Insights?.[0]?.description,
            focusArea: viewModel?.focusArea?.title,
            overallScore: viewModel?.overallScore,
          }}
        />
      )}
    </div>
  );
}
